import React from 'react';
import { ClickableTicker } from '../TradingViewModal';

interface SymbolMetadata {
  sector?: string;
  industry?: string;
  price?: number;
  volume?: number;
  market_cap?: number;
  added_at?: string;
}

interface SymbolCardProps {
  symbol: string;
  metadata: SymbolMetadata;
  patternNames: string[];
  isPremarket: boolean;
}

const SECTOR_COLORS: Record<string, string> = {
  Technology: '#2196f3',
  Healthcare: '#4caf50',
  'Consumer Cyclical': '#ff9800',
  'Financial Services': '#9c27b0',
  'Communication Services': '#00bcd4',
  Energy: '#f44336',
  Industrials: '#607d8b',
  'Consumer Defensive': '#795548',
  'Real Estate': '#e91e63',
  Utilities: '#009688',
  'Basic Materials': '#ff5722',
};

function formatVolume(vol: number | undefined): string {
  if (!vol) return '--';
  if (vol >= 1_000_000_000) return `${(vol / 1_000_000_000).toFixed(1)}B`;
  if (vol >= 1_000_000) return `${(vol / 1_000_000).toFixed(1)}M`;
  if (vol >= 1_000) return `${(vol / 1_000).toFixed(0)}K`;
  return vol.toString();
}

function formatMarketCap(cap: number | undefined): string {
  if (!cap) return '--';
  if (cap >= 1_000_000_000_000) return `$${(cap / 1_000_000_000_000).toFixed(1)}T`;
  if (cap >= 1_000_000_000) return `$${(cap / 1_000_000_000).toFixed(1)}B`;
  if (cap >= 1_000_000) return `$${(cap / 1_000_000).toFixed(0)}M`;
  return `$${cap.toLocaleString()}`;
}

const SymbolCard: React.FC<SymbolCardProps> = ({ symbol, metadata, patternNames, isPremarket }) => {
  const sectorColor = SECTOR_COLORS[metadata.sector || ''] || '#6b7280';

  return (
    <div className="rounded-lg border bg-card p-3 hover:bg-accent/50 transition-colors">
      <div className="flex flex-col gap-2">
        {/* Ticker + Premarket badge */}
        <div className="flex items-center gap-2">
          <ClickableTicker symbol={symbol}>
            <span className="font-bold text-sm text-foreground">{symbol}</span>
          </ClickableTicker>
          {isPremarket && (
            <span className="inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-[0.65rem] font-semibold text-amber-600 dark:text-amber-400">
              Premarket
            </span>
          )}
        </div>

        {/* Price */}
        <span className="text-sm font-medium text-foreground">
          {metadata.price ? `$${metadata.price.toFixed(2)}` : '--'}
        </span>

        {/* Sector chip */}
        {metadata.sector && (
          <span
            className="inline-flex items-center rounded-full px-2 py-0.5 text-[0.65rem] font-medium text-white"
            style={{ backgroundColor: sectorColor }}
          >
            {metadata.sector}
          </span>
        )}

        {/* Volume + Market Cap */}
        <div className="flex gap-3 text-xs text-muted-foreground">
          <div>
            <span className="font-semibold">VOL </span>
            <span>{formatVolume(metadata.volume)}</span>
          </div>
          <div>
            <span className="font-semibold">MCAP </span>
            <span>{formatMarketCap(metadata.market_cap)}</span>
          </div>
        </div>

        {/* Pattern chips */}
        <div className="flex flex-wrap gap-1">
          {patternNames.map((name) => (
            <span
              key={name}
              className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[0.65rem] font-medium text-primary"
            >
              {name}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SymbolCard;
